import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, Typography, Paper, Alert, AlertTitle, Chip, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faCode, faAlignLeft, faTriangleExclamation, faUpRightFromSquare, faEnvelopeOpenText, faImage } from '@fortawesome/free-solid-svg-icons';
import client from '../api/client';
import { messageFromAxiosErrorResponse } from '../utils/axiosErrorMessage';
import { buildGmailThreadDeepLink } from '../utils/gmailWebUrls';
import { sanitizeEmailHtml } from '../utils/emailHtml';
import type { WorkItemSafety } from '../api/workItems';

interface EmailContent {
    status: 'FETCHED' | 'FETCHING' | 'FAILED' | 'NOT_FETCHED';
    /** Gmail conversation thread id (for web UI deep links; not the same as Gmail API message id). */
    gmail_thread_id?: string | null;
    body_text: string | null;
    body_html: string | null;
    attachments: Array<{
        filename: string;
        mime_type: string;
        size: number;
        attachment_id: string;
    }>;
    phishing_analysis: {
        /** Explicit risk when heuristic/LLM completed; null when assessment did not complete (avoid false negatives). */
        is_phishing: boolean | null;
        reason: string;
        score: number | null;
        risk_level?: string;
        analysis_type?: string;
    } | null;
    fetched_at: string | null;
    /** C29-1 T5: the item has a current "Looks safe" (covers every message on it). */
    safety_released?: boolean;
    safety?: WorkItemSafety | null;
}

interface WorkItemEmailViewerProps {
    workItemId: string;
    gmailMessageId: string | null | undefined;
    autoExpand?: boolean;
    /** Drop the top divider when the viewer already sits inside an Original email section. */
    embedded?: boolean;
    allowReply?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    onMailboxGmailAuthIssue?: () => void;
    /**
     * C29-1: changes when the item's safety changes (a verdict, Undo, a new hold). The
     * viewer then reloads: the message it opens and the released banner both follow it.
     */
    safetyKey?: string;
    onSafetyChange?: (safety: WorkItemSafety | null) => void;
}

function attachmentEndpoint(workItemId: string, attachmentId: string): string {
    return `/work-items/${workItemId}/email/attachments/${attachmentId}`;
}

function phishingAnalysisIsDegraded(pa: NonNullable<EmailContent['phishing_analysis']>): boolean {
    if (pa.analysis_type === 'skipped') {
        return true;
    }
    return pa.is_phishing === null || pa.risk_level === 'unknown';
}

/** Gmail `#inbox/<id>` opens a conversation by **thread** id; message ids open the wrong view. */
/**
 * Backend contract: `gmail_web_authuser_index` is always emitted as `integer | null`
 * by the navigation/email endpoints (Pydantic model with mode="before" validator).
 * Anything else (numeric strings, floats, booleans, out-of-range) collapses to null
 * — strict parsing keeps Gmail link routing predictable even if the contract drifts.
 */
function parseMailboxAuthuserIndex(raw: unknown): number | null {
    if (raw === null || raw === undefined) {
        return null;
    }
    if (typeof raw !== 'number' || !Number.isInteger(raw)) {
        return null;
    }
    if (raw < 0 || raw > 7) {
        return null;
    }
    return raw;
}

function parseMailboxEmail(raw: unknown): string | null {
    if (typeof raw !== 'string') {
        return null;
    }
    const trimmed = raw.trim();
    return trimmed === '' ? null : trimmed;
}

/** Loads attachment bytes via axios (Bearer token); raw <img src> cannot authenticate. */
const AuthenticatedInlineImage: React.FC<{
    workItemId: string;
    attachmentId: string;
    alt: string;
    onMailboxGmailAuthIssue?: () => void;
}> = ({ workItemId, attachmentId, alt, onMailboxGmailAuthIssue }) => {
    const [src, setSrc] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);
    const [loadingImg, setLoadingImg] = useState(true);
    const blobUrlRef = useRef<string | null>(null);
    const authRef = useRef(onMailboxGmailAuthIssue);
    authRef.current = onMailboxGmailAuthIssue;

    useEffect(() => {
        let cancelled = false;
        setSrc(null);
        setFailed(false);
        setLoadingImg(true);
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }

        (async () => {
            try {
                const res = await client.get(attachmentEndpoint(workItemId, attachmentId), {
                    responseType: 'blob',
                });
                if (cancelled) return;
                blobUrlRef.current = URL.createObjectURL(res.data);
                setSrc(blobUrlRef.current);
            } catch (err: any) {
                if (!cancelled) {
                    if (err.response?.status === 401) {
                        authRef.current?.();
                    }
                    setFailed(true);
                }
            } finally {
                if (!cancelled) setLoadingImg(false);
            }
        })();

        return () => {
            cancelled = true;
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [workItemId, attachmentId]);

    if (loadingImg && !failed) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }
    if (failed || !src) {
        return (
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', p: 1, display: 'block' }}>
                Preview unavailable (open the file below to download).
            </Typography>
        );
    }
    return (
        <img
            src={src}
            alt={alt}
            style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
        />
    );
};

const WorkItemEmailViewer: React.FC<WorkItemEmailViewerProps> = ({
    workItemId,
    gmailMessageId,
    autoExpand = false,
    embedded = false,
    allowReply = true,
    onExpandedChange,
    onMailboxGmailAuthIssue,
    safetyKey,
    onSafetyChange,
}) => {
    const [content, setContent] = useState<EmailContent | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'text' | 'html'>('text');
    const [expanded, setExpanded] = useState(autoExpand);
    const [gmailThreadId, setGmailThreadId] = useState<string | null>(null);
    const [mailboxEmail, setMailboxEmail] = useState<string | null>(null);
    const [gmailWebAuthuserIndex, setGmailWebAuthuserIndex] = useState<number | null>(null);
    const [sender, setSender] = useState<string | null>(null);
    const currentItemRef = useRef(workItemId);
    currentItemRef.current = workItemId;
    const currentSafetyKeyRef = useRef(safetyKey);
    currentSafetyKeyRef.current = safetyKey;
    const fetchSequenceRef = useRef(0);
    const safetyChangeRef = useRef(onSafetyChange);
    safetyChangeRef.current = onSafetyChange;
    const [copyStatus, setCopyStatus] = useState<string | null>(null);
    const [attachmentHint, setAttachmentHint] = useState<string | null>(null);
    const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<string | null>(null);
    const authIssueRef = useRef(onMailboxGmailAuthIssue);
    authIssueRef.current = onMailboxGmailAuthIssue;

    const gmailUrl = gmailMessageId && gmailThreadId && (mailboxEmail || gmailWebAuthuserIndex !== null)
        ? buildGmailThreadDeepLink({
              threadId: gmailThreadId,
              mailboxEmail,
              gmailWebAuthuserIndex,
          })
        : '';

    // A current "Looks safe" on the item releases the red banner and Open in Gmail for
    // whichever message is open; a new flagged message makes the verdict stale again.
    const flaggedHigh = content?.safety_released !== true && (
        content?.safety?.level === 'high'
        || content?.safety?.held === true
        ||
        content?.phishing_analysis?.is_phishing === true
        || ['high', 'critical'].includes(content?.phishing_analysis?.risk_level || '')
    );
    const canOpenMailbox = allowReply && !flaggedHigh && Boolean(gmailUrl);

    useEffect(() => {
        setContent(null);
        setError(null);
        setCopyStatus(null);
        setSender(null);
        setExpanded(autoExpand);
        // Reset on identity; expansion changes are handled separately below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workItemId]);

    useEffect(() => {
        if (!gmailMessageId) {
            setGmailThreadId(null);
            setMailboxEmail(null);
            setGmailWebAuthuserIndex(null);
            return;
        }
        let cancelled = false;
        setGmailThreadId(null);
        setMailboxEmail(null);
        setGmailWebAuthuserIndex(null);
        (async () => {
            try {
                const res = await client.get(`/work-items/${workItemId}/email/navigation`);
                if (cancelled) return;
                const tid = res.data?.gmail_thread_id as string | null | undefined;
                setGmailThreadId(tid?.trim() ? tid.trim() : null);
                setMailboxEmail(parseMailboxEmail(res.data?.mailbox_email_address));
                setSender(typeof res.data?.sender === 'string' ? res.data.sender : null);
                setGmailWebAuthuserIndex(parseMailboxAuthuserIndex(res.data?.gmail_web_authuser_index));
            } catch {
                // Reading remains available; never guess the mailbox if navigation fails.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [workItemId, gmailMessageId, safetyKey]);

    const downloadAttachmentBlob = useCallback(
        async (att: EmailContent['attachments'][number]) => {
            setAttachmentHint(null);
            try {
                const res = await client.get(attachmentEndpoint(workItemId, att.attachment_id), {
                    responseType: 'blob',
                });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = att.filename || 'attachment';
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } catch (err: any) {
                const status = err.response?.status as number | undefined;
                if (status === 401) {
                    authIssueRef.current?.();
                }
                const msg = await messageFromAxiosErrorResponse(
                    err.response,
                    err.message || 'Could not download attachment.',
                );
                setAttachmentHint(msg);
            }
        },
        [workItemId],
    );

    const fetchEmail = useCallback(async () => {
        const sequence = ++fetchSequenceRef.current;
        const isCurrent = () => currentItemRef.current === workItemId
            && currentSafetyKeyRef.current === safetyKey
            && fetchSequenceRef.current === sequence;
        setLoading(true);
        setError(null);
        try {
            const res = await client.get(`/work-items/${workItemId}/email`);
            if (!res.data || typeof res.data !== 'object') {
                throw new Error("Received empty or invalid data from server.");
            }
            if (!isCurrent()) return;
            setContent(res.data);
            setSender(typeof res.data?.sender === 'string' ? res.data.sender : null);
            const tid = res.data?.gmail_thread_id as string | undefined;
            setGmailThreadId(tid?.trim() ? tid.trim() : null);
            setMailboxEmail(parseMailboxEmail(res.data?.mailbox_email_address));
            setGmailWebAuthuserIndex(parseMailboxAuthuserIndex(res.data?.gmail_web_authuser_index));
            setExpanded(true);
            if ('safety' in res.data) safetyChangeRef.current?.(res.data.safety ?? null);
        } catch (err: any) {
            if (!isCurrent()) return;
            const msg = err.response?.data?.detail || err.message || "Failed to load email content.";
            setError(msg);
            setExpanded(true); // Still expand so user sees the error
            // Only 401 implies persisted mailbox oauth flag + bootstrap aggregate update.
            const status = err.response?.status as number | undefined;
            if (status === 401) {
                authIssueRef.current?.();
            }
        } finally {
            if (isCurrent()) setLoading(false);
        }
    }, [workItemId, safetyKey]);

    const targetKey = JSON.stringify([workItemId, gmailMessageId, safetyKey]);
    const targetKeyRef = useRef(targetKey);
    useEffect(() => {
        const changed = targetKeyRef.current !== targetKey;
        targetKeyRef.current = targetKey;
        if (changed) {
            setContent(null);
            setError(null);
        }
        if (gmailMessageId && ((changed && (expanded || loading)) || (autoExpand && (changed || !content)))) {
            void fetchEmail();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetKey, autoExpand, fetchEmail]);

    useEffect(() => {
        onExpandedChange?.(Boolean(gmailMessageId) && expanded);
    }, [expanded, gmailMessageId, onExpandedChange]);

    const handleToggle = () => {
        if (expanded) {
            setExpanded(false);
            setError(null); // Clear error on close so retry button works
        } else {
            if (content) {
                setExpanded(true);
            } else {
                fetchEmail();
            }
        }
    };

    const isImage = (mimeType: string) => mimeType.startsWith('image/');

    if (!gmailMessageId) {
        return null;
    }

    const copyGmailLink = async () => {
        if (!gmailUrl) return;
        try {
            if (!navigator?.clipboard?.writeText) {
                throw new Error('Clipboard is unavailable in this browser.');
            }
            await navigator.clipboard.writeText(gmailUrl);
            setCopyStatus('Email link copied.');
        } catch (err: any) {
            setCopyStatus(err.message || 'Could not copy the Gmail link.');
        }
    };

    const chromeSx = embedded
        ? { mt: 0 }
        : { mt: 3, borderTop: '1px solid var(--color-border)', pt: 2 };

    if (!expanded) {
        return (
            <Box data-testid="WB_EMAIL_VIEWER" sx={{ ...chromeSx, '& .MuiButton-root': { minHeight: 44 } }}>
                {sender && <Typography variant="body2" sx={{ mb: 1, color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>From: {sender}</Typography>}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        data-testid="WB_EMAIL_VIEW_ORIGINAL"
                        onClick={handleToggle}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} /> : <FontAwesomeIcon icon={faEnvelopeOpenText} />}
                        sx={{
                            flex: 1,
                            color: 'var(--color-text-secondary)',
                            borderColor: 'var(--color-border)',
                            '&:hover': { borderColor: 'var(--color-accent)', color: 'var(--color-accent-text)' },
                            textTransform: 'none'
                        }}
                    >
                        {loading ? 'Opening...' : 'View original email'}
                    </Button>
                </Box>
                {copyStatus && (
                    <Alert
                        severity={copyStatus.includes('Copied') ? 'success' : 'warning'}
                        sx={{ mt: 1, bgcolor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)' }}
                        onClose={() => setCopyStatus(null)}
                    >
                        {copyStatus}
                    </Alert>
                )}
            </Box>
        );
    }

    return (
        // Inline-size containment keeps anything inside (a sender's fixed-width HTML, a long
        // attachment name) from widening the drawer: the viewer is as wide as its slot.
        <Box data-testid="WB_EMAIL_VIEWER" sx={{ ...chromeSx, contain: 'inline-size' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: 'var(--color-text-primary)', fontSize: '1rem' }}>Original email</Typography>
                <Button size="small" onClick={handleToggle} sx={{ color: 'var(--color-text-muted)' }}>
                    Hide
                </Button>
            </Box>

            {canOpenMailbox && <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                    size="small"
                    variant="outlined"
                    component="a"
                    data-testid="WB_EMAIL_OPEN_IN_GMAIL"
                    href={gmailUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
                    sx={{ color: 'var(--color-text-primary)', borderColor: 'var(--color-border)', textTransform: 'none' }}
                >
                    Open in Gmail
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    data-testid="WB_EMAIL_COPY_REPLY_LINK"
                    onClick={copyGmailLink}
                    startIcon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
                    sx={{ color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)', textTransform: 'none' }}
                >
                    Copy email link
                </Button>
            </Box>}

            {error && (
                <Alert severity="error" sx={{ mb: 2, bgcolor: 'rgba(var(--color-danger-rgb), 0.1)', color: 'var(--color-text-primary)' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2">{error}</Typography>
                        {(error.toLowerCase().includes('authenticate') || error.toLowerCase().includes('token') || error.includes('401')) && (
                            <Typography variant="caption" sx={{ mt: 1, p: 1, bgcolor: 'rgba(var(--color-danger-rgb), 0.1)', borderRadius: 1, fontStyle: 'italic' }}>
                                💡 Tip: It looks like your Gmail session has expired. Go to <strong>Profile &gt; Gmail Integrations</strong> to reconnect your account.
                            </Typography>
                        )}
                    </Box>
                </Alert>
            )}

            {content && (
                <Box>
                    {/* Phishing: only when heuristic completed and flagged */}
                    {flaggedHigh && (
                        <Alert
                            severity="warning"
                            variant="filled"
                            icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                            sx={{ mb: 2 }}
                        >
                            <AlertTitle>Potential phishing detected</AlertTitle>
                            {content.phishing_analysis?.reason}
                        </Alert>
                    )}

                    {/* Analyzer skipped / unknown — explicit degraded state (not “safe”) */}
                    {content.phishing_analysis &&
                        content.phishing_analysis.is_phishing !== true &&
                        phishingAnalysisIsDegraded(content.phishing_analysis) && (
                            <Alert
                                severity="info"
                                variant="outlined"
                                icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                                sx={{
                                    mb: 2,
                                    borderColor: 'var(--color-border)',
                                    bgcolor: 'var(--color-bg-tertiary)',
                                    color: 'var(--color-text-primary)',
                                }}
                            >
                                <AlertTitle>Safety scan unavailable</AlertTitle>
                                {content.phishing_analysis.reason}
                            </Alert>
                        )}

                    {/* Metadata & Actions */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
                        {mailboxEmail && <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>To: {mailboxEmail}</Typography>}
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                            size="small"
                            variant={viewMode === 'text' ? 'contained' : 'outlined'}
                            onClick={() => setViewMode('text')}
                            startIcon={<FontAwesomeIcon icon={faAlignLeft} />}
                            sx={viewMode === 'text' ? { bgcolor: 'var(--color-accent)', color: 'var(--color-on-accent)', '&:hover': { bgcolor: 'var(--color-accent-hover)', color: 'var(--color-on-accent-hover)' } } : { color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }}
                        >
                            Text
                        </Button>
                        <Button
                            size="small"
                            variant={viewMode === 'html' ? 'contained' : 'outlined'}
                            onClick={() => setViewMode('html')}
                            startIcon={<FontAwesomeIcon icon={faCode} />}
                            sx={viewMode === 'html' ? { bgcolor: 'var(--color-accent)', color: 'var(--color-on-accent)', '&:hover': { bgcolor: 'var(--color-accent-hover)', color: 'var(--color-on-accent-hover)' } } : { color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }}
                        >
                            Original layout
                        </Button>
                    </Box>

                    {/* Content View */}
                    <Paper
                        sx={{
                            p: 2,
                            bgcolor: 'var(--color-bg-tertiary)',
                            borderRadius: 2,
                            border: '1px solid var(--color-border)',
                            overflow: 'visible',
                        }}
                    >
                        {viewMode === 'text' ? (
                            <Typography
                                variant="body2"
                                sx={{
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                    color: 'var(--color-text-primary)',
                                    lineHeight: 1.6,
                                }}
                            >
                                {content.body_text || "No text content available."}
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    bgcolor: 'var(--color-email-paper)',
                                    color: 'var(--color-email-ink)',
                                    p: 2,
                                    borderRadius: 1,
                                    border: '1px solid var(--color-border)',
                                    '& a': { color: 'var(--color-email-link)' },
                                    '& p, & li, & div': { lineHeight: 1.6 },
                                    // A sender's fixed-width layout (newsletters are often 600–760 px)
                                    // scrolls sideways inside this box. Images shrink to fit (the global
                                    // reset), keeping the shape of their width/height attributes
                                    // (keepSizedEmailImages).
                                    maxWidth: '100%',
                                    overflowX: 'auto',
                                    // Paint containment makes this box the containing block for the
                                    // email's `position: fixed` content and clips it here, so an email
                                    // cannot draw over the rest of the app.
                                    contain: 'paint',
                                }}
                                // Sanitized HTML: no <style>, no form controls (utils/emailHtml.ts)
                                dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(content.body_html || "") }}
                            />
                        )}
                    </Paper>

                    {/* Attachments */}
                    {content.attachments && content.attachments.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mb: 1 }}>
                                <FontAwesomeIcon icon={faPaperclip} /> {content.attachments.length} Attachments
                            </Typography>
                            {attachmentHint && (
                                <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setAttachmentHint(null)}>
                                    {attachmentHint}
                                </Alert>
                            )}

                            {/* Inline Images */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                                {content.attachments.filter(att => isImage(att.mime_type)).map((att, idx) => (
                                    <Box key={`img-${att.attachment_id}-${idx}`} sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
                                        <AuthenticatedInlineImage
                                            workItemId={workItemId}
                                            attachmentId={att.attachment_id}
                                            alt={att.filename}
                                            onMailboxGmailAuthIssue={onMailboxGmailAuthIssue}
                                        />
                                        <Box sx={{ p: 1, bgcolor: 'var(--color-bg-secondary)', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FontAwesomeIcon icon={faImage} size="xs" style={{ color: 'var(--color-text-muted)' }} />
                                            <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>{att.filename}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {content.attachments.map((att, idx) => (
                                    <Chip
                                        key={`${att.attachment_id}-${idx}`}
                                        label={
                                            downloadingAttachmentId === att.attachment_id
                                                ? 'Downloading…'
                                                : `${att.filename} (${Math.round(att.size / 1024)}KB)`
                                        }
                                        onClick={() => {
                                            if (downloadingAttachmentId) return;
                                            setDownloadingAttachmentId(att.attachment_id);
                                            void downloadAttachmentBlob(att).finally(() => setDownloadingAttachmentId(null));
                                        }}
                                        disabled={downloadingAttachmentId === att.attachment_id}
                                        clickable
                                        sx={{
                                            bgcolor: 'var(--color-bg-card)',
                                            color: 'var(--color-accent-text)',
                                            border: '1px solid var(--color-border)',
                                            '&:hover': { bgcolor: 'var(--color-bg-card-hover)' }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default WorkItemEmailViewer;
